import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CategoryFilter, Flex, Page, ProviderFilter, theme, Typo } from '../../ui';
import { toRgba } from '../../ui/theme/colors';
import PageSection from '../../ui/Page/PageSection';
import { useSelector } from 'react-redux';
import { selectCurrentStoreHideVat } from '../webshop/globalSlice';
import { media } from '../../utils/media';
import { useCatalogState } from './hooks';
import CatalogInventory from './components/CatalogInventory';
import SelectedCoursesSidebar from './components/SelectedCoursesSidebar';

const StickyFilterRow = styled(Flex)`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${toRgba(theme.colors.NEU_50, 0.5)};
  padding: 24px 0 12px 0;
  margin: 0 -1rem;
  padding-left: 1rem;
  padding-right: 1rem;

  ${media.lessThan('phablet')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CategoryFilterWrapper = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

const ContentLayout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  width: 100%;

  ${media.lessThan('tablet')} {
    flex-direction: column;
  }
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderSection = styled(Flex)`
  margin-bottom: 32px;
  margin-top: 12px;
`;

const NetworkCatalog = () => {
  const { t } = useTranslation();
  const hideVAT = useSelector(selectCurrentStoreHideVat);
  const {
    categories,
    providers,
    selectedProviderId,
    setSelectedProviderId,
    selectedCategorySlug,
    setSelectedCategorySlug,
    filteredCourses,
    selectedCourses,
    selectedIds,
    handleToggle,
    handleRemove,
    handleClear,
    toggleSort,
    currency,
  } = useCatalogState();

  return (
    <Page>
      <PageSection>
        <HeaderSection direction='column' align='flex-start' width='100%' gap='20'>
          <Flex align='center' gap='5'>
            <Typo.SectionHeader $caps fontWeight='300' color={theme.colors.PRIMARY}>
              {t('coursioNetwork.catalog.title')}
            </Typo.SectionHeader>
            <Typo.ContentBigHeader color={theme.colors.COURSIO}>:</Typo.ContentBigHeader>
            <Typo.BigText>{t('coursioNetwork.catalog.subtitle')}</Typo.BigText>
          </Flex>
          <Flex direction='column' align='flex-start' width='100%'>
            <Typo.Text>{t('coursioNetwork.catalog.introLine1')}</Typo.Text>
            <Typo.Text>{t('coursioNetwork.catalog.introLine2')}</Typo.Text>
            <Typo.Text>{t('coursioNetwork.catalog.introLine3')}</Typo.Text>
          </Flex>
          <Typo.Text>{t('coursioNetwork.catalog.selectionHint')}</Typo.Text>
        </HeaderSection>
        <StickyFilterRow align='center' gap='16' width='100%' justify='space-between'>
          {categories.length > 0 && (
            <CategoryFilterWrapper>
              <CategoryFilter categories={categories} selectedCategorySlug={selectedCategorySlug} onSelect={setSelectedCategorySlug} />
            </CategoryFilterWrapper>
          )}
          <ProviderFilter providers={providers} selectedProviderId={selectedProviderId} onSelect={setSelectedProviderId} />
        </StickyFilterRow>
        <ContentLayout>
          <MainColumn>
            <CatalogInventory courses={filteredCourses} selectedIds={selectedIds} hideVAT={hideVAT} onToggle={handleToggle} onSort={toggleSort} />
          </MainColumn>

          <SelectedCoursesSidebar selectedCourses={selectedCourses} currency={currency} hideVAT={hideVAT} onRemove={handleRemove} onClear={handleClear} />
        </ContentLayout>
      </PageSection>
    </Page>
  );
};

export default NetworkCatalog;
